import { useContext, useMemo, useState } from 'react';
import { T, useGT } from 'gt-react';
import { useListStepTypes } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import { Badge } from 'src/components/ui/badge';
import { Input } from 'src/components/ui/input';
import { AuthContext } from '@/context/auth.context';
import { isAdmin } from '@/utils/user';
import { useViewMode } from '@/context/view-mode.context';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import {
  CircleNotchIcon,
  DotsSixVerticalIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  ProhibitIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import {
  ACTION_INPUT_STEP_TYPE,
  ACTION_OUTPUT_STEP_TYPE,
  WORKFLOW_ACTION_STEP_TYPE,
} from '@erp/workflows';
import { cn } from 'src/lib/utils';
import type { ListStepTypesResponse } from '@erp/rpc';
import { CategoryBadge } from 'src/lib/workflows/CategoryBadge';
import {
  erpCategoryAccordionKey,
  platformCategoryKey,
} from 'src/lib/workflows/libraryModuleLabels';
import {
  ERP_MODULE_ORDER,
  ERP_MODULE_STYLES,
  isErpLibraryModule,
  type ErpLibraryModule,
} from 'src/lib/workflows/libraryModuleStyles';

type StepType = ListStepTypesResponse['stepTypes'][0];

function sortCategoriesAlphabetical(categories: string[]): string[] {
  return [...categories].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
}

function getLibraryCategory(stepType: StepType): string {
  if (
    stepType.type === ACTION_INPUT_STEP_TYPE ||
    stepType.type === ACTION_OUTPUT_STEP_TYPE ||
    stepType.type === WORKFLOW_ACTION_STEP_TYPE
  ) {
    return 'Reuse';
  }

  return stepType.category;
}

interface DraggableStepCardProps {
  stepType: StepType;
  compact?: boolean;
}

function DraggableStepCard({ stepType, compact }: DraggableStepCardProps) {
  const isDisabled = stepType.visibility?.visibility === 'disabled';

  const handleDragStart = (event: React.DragEvent) => {
    if (isDisabled) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.setData(
      'application/workflow-step-type',
      JSON.stringify(stepType),
    );
    event.dataTransfer.effectAllowed = 'move';
  };

  const card = (
    <div
      draggable={!isDisabled}
      onDragStart={handleDragStart}
      className={cn(
        'group flex items-center gap-2 rounded-md border transition-all duration-150',
        compact ? 'p-1.5' : 'p-2',
        isDisabled
          ? 'opacity-50 border-slate-200 dark:border-slate-700 cursor-not-allowed bg-muted/30'
          : [
              'cursor-grab active:cursor-grabbing',
              'bg-white dark:bg-slate-800 hover:shadow-md',
              'hover:scale-[1.01] hover:border-primary/50',
            ],
        !isDisabled &&
          stepType.deprecated &&
          'opacity-60 border-amber-300 dark:border-amber-600',
        !isDisabled &&
          stepType.visibility?.visibility === 'restricted' &&
          'opacity-50 border-slate-300 dark:border-slate-600 cursor-not-allowed',
        !isDisabled &&
          stepType.visibility?.visibility === 'hidden' &&
          'opacity-40 border-red-300 dark:border-red-600',
      )}
      title={isDisabled ? undefined : stepType.deprecated?.reason}
    >
      <DotsSixVerticalIcon
        className={cn(
          'w-4 h-4 flex-shrink-0',
          isDisabled
            ? 'text-muted-foreground/30'
            : 'text-muted-foreground/50 group-hover:text-muted-foreground',
        )}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <CategoryBadge
            category={stepType.category}
            size={compact ? 'sm' : 'md'}
          />
          <span
            className={cn(
              'font-medium truncate',
              compact ? 'text-xs' : 'text-sm',
              isDisabled && 'text-muted-foreground',
            )}
          >
            {stepType.name}
          </span>
          {isDisabled && (
            <ProhibitIcon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          )}
          {!isDisabled && stepType.deprecated && (
            <WarningIcon className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
          )}
        </div>
        {!compact && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {stepType.description}
          </p>
        )}
      </div>
    </div>
  );

  if (isDisabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>{card}</div>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs">
          {stepType.visibility?.disabledReason?.trim() ? (
            stepType.visibility.disabledReason
          ) : (
            <T context="tooltip when workflow step is disabled for non-admin users">
              Please contact your administrator to enable this feature.
            </T>
          )}
        </TooltipContent>
      </Tooltip>
    );
  }

  return card;
}

interface PlatformCategoryAccordionProps {
  categories: string[];
  stepsByCategory: Map<string, StepType[]>;
  openKeys: string[];
  onOpenChange: (keys: string[]) => void;
  compact?: boolean;
}

function PlatformCategoryAccordion({
  categories,
  stepsByCategory,
  openKeys,
  onOpenChange,
  compact,
}: PlatformCategoryAccordionProps) {
  return (
    <Accordion
      type="multiple"
      value={openKeys}
      onValueChange={onOpenChange}
      className="px-1"
    >
      {categories.map((category) => {
        const steps = stepsByCategory.get(category) ?? [];
        const key = platformCategoryKey(category);

        return (
          <AccordionItem key={key} value={key} className="border-b-0">
            <AccordionTrigger className="py-2 hover:no-underline">
              <div className="flex items-center gap-2 min-w-0">
                <CategoryBadge category={category} size="md" />
                <span className="text-sm font-medium truncate">{category}</span>
                <Badge variant="secondary" className="text-xs shrink-0">
                  {steps.length}
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-2">
              <div className="space-y-1.5">
                {steps.map((stepType) => (
                  <DraggableStepCard
                    key={stepType.type}
                    stepType={stepType}
                    compact={compact}
                  />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

interface ErpModuleSectionProps {
  module: ErpLibraryModule;
  stepsByCategory: Map<string, StepType[]>;
  isOpen: boolean;
  openCategoryKeys: string[];
  onModuleOpenChange: (open: boolean) => void;
  onCategoryOpenChange: (keys: string[]) => void;
}

function ErpModuleSection({
  module,
  stepsByCategory,
  isOpen,
  openCategoryKeys,
  onModuleOpenChange,
  onCategoryOpenChange,
}: ErpModuleSectionProps) {
  const style = ERP_MODULE_STYLES[module];
  const ModuleIcon = style.Icon;
  const categories = sortCategoriesAlphabetical([...stepsByCategory.keys()]);
  const stepCount = categories.reduce(
    (sum, cat) => sum + (stepsByCategory.get(cat)?.length ?? 0),
    0,
  );

  return (
    <div
      className={cn(
        'rounded-lg border overflow-hidden transition-shadow',
        style.border,
        isOpen && 'shadow-sm',
      )}
    >
      <button
        type="button"
        onClick={() => onModuleOpenChange(!isOpen)}
        className={cn(
          'w-full flex items-center gap-2.5 px-2.5 py-2 text-left transition-colors',
          style.headerBg,
          'hover:brightness-[0.98] dark:hover:brightness-110',
        )}
      >
        <span
          className={cn('w-1 self-stretch rounded-full shrink-0', style.accent)}
        />
        <span
          className={cn(
            'flex items-center justify-center w-8 h-8 rounded-lg shrink-0',
            style.iconBg,
          )}
        >
          <ModuleIcon
            className={cn('w-4 h-4', style.iconColor)}
            weight="duotone"
          />
        </span>
        <span className="flex-1 min-w-0 text-sm font-semibold tracking-tight truncate">
          {style.label}
        </span>
        <span
          className={cn(
            'text-xs font-medium tabular-nums px-2 py-0.5 rounded-full shrink-0',
            style.badge,
          )}
        >
          {stepCount}
        </span>
      </button>

      {isOpen && (
        <div className="px-2 pb-2 pt-0.5 border-t border-inherit/50 bg-muted/20 dark:bg-muted/10">
          <Accordion
            type="multiple"
            value={openCategoryKeys}
            onValueChange={onCategoryOpenChange}
          >
            {categories.map((category) => {
              const steps = stepsByCategory.get(category) ?? [];
              const categoryKey = erpCategoryAccordionKey(module, category);

              return (
                <AccordionItem
                  key={categoryKey}
                  value={categoryKey}
                  className="border-b-0"
                >
                  <AccordionTrigger className="py-1.5 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <CategoryBadge category={category} size="sm" />
                      <span className="text-xs font-medium">{category}</span>
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 h-4 font-normal"
                      >
                        {steps.length}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-1.5 pt-0">
                    <div className="space-y-1.5 pl-0.5">
                      {steps.map((stepType) => (
                        <DraggableStepCard
                          key={stepType.type}
                          stepType={stepType}
                          compact
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      )}
    </div>
  );
}

interface NodeLibraryContentProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

export function NodeLibraryContent({
  searchQuery,
  onSearchChange,
}: NodeLibraryContentProps) {
  const gt = useGT();
  const { data, isLoading, error } = useListStepTypes();
  const [openPlatformCategories, setOpenPlatformCategories] = useState<
    string[]
  >([]);
  const [openErpModules, setOpenErpModules] = useState<ErpLibraryModule[]>([]);
  const [openErpCategoryKeys, setOpenErpCategoryKeys] = useState<string[]>([]);
  const [showDeprecated, setShowDeprecated] = useState(false);
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const gently = isAdmin(currentUser, viewMode);

  const {
    platformByCategory,
    platformCategories,
    erpByModule,
    erpModules,
    allPlatformKeys,
    allErpModuleKeys,
    allErpCategoryKeys,
  } = useMemo(() => {
    const empty = {
      platformByCategory: new Map<string, StepType[]>(),
      platformCategories: [] as string[],
      erpByModule: new Map<ErpLibraryModule, Map<string, StepType[]>>(),
      erpModules: [] as ErpLibraryModule[],
      allPlatformKeys: [] as string[],
      allErpModuleKeys: [] as ErpLibraryModule[],
      allErpCategoryKeys: [] as string[],
    };

    if (!data?.stepTypes) return empty;

    const visible = data.stepTypes.filter((st) => {
      if (!showDeprecated && st.deprecated) return false;
      if (!gently && st.visibility?.visibility === 'hidden') return false;
      if (!gently && st.category === 'Debug') return false;
      return true;
    });

    const query = searchQuery.toLowerCase().trim();
    const filtered = query
      ? visible.filter(
          (st) =>
            st.name.toLowerCase().includes(query) ||
            st.description.toLowerCase().includes(query) ||
            st.type.toLowerCase().includes(query),
        )
      : visible;

    const platformByCategory = new Map<string, StepType[]>();
    const erpByModule = new Map<ErpLibraryModule, Map<string, StepType[]>>();

    filtered.forEach((stepType) => {
      const { libraryModule } = stepType;
      const category = getLibraryCategory(stepType);

      if (libraryModule === 'Platform') {
        if (!platformByCategory.has(category)) {
          platformByCategory.set(category, []);
        }
        platformByCategory.get(category)?.push(stepType);
        return;
      }

      if (!isErpLibraryModule(libraryModule)) return;

      if (!erpByModule.has(libraryModule)) {
        erpByModule.set(libraryModule, new Map());
      }
      const byCategory = erpByModule.get(libraryModule);
      if (!byCategory) return;
      if (!byCategory.has(category)) {
        byCategory.set(category, []);
      }
      byCategory.get(category)?.push(stepType);
    });

    const sortSteps = (steps: StepType[]) =>
      steps.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      );

    for (const steps of platformByCategory.values()) sortSteps(steps);
    for (const byCategory of erpByModule.values()) {
      for (const steps of byCategory.values()) sortSteps(steps);
    }

    const platformCategories = sortCategoriesAlphabetical([
      ...platformByCategory.keys(),
    ]);
    const erpModules = ERP_MODULE_ORDER.filter((m) => erpByModule.has(m));

    const allPlatformKeys = platformCategories.map(platformCategoryKey);
    const allErpModuleKeys = [...erpModules];
    const allErpCategoryKeys: string[] = [];
    for (const module of erpModules) {
      const byCategory = erpByModule.get(module);
      if (!byCategory) continue;
      for (const category of sortCategoriesAlphabetical([
        ...byCategory.keys(),
      ])) {
        allErpCategoryKeys.push(erpCategoryAccordionKey(module, category));
      }
    }

    return {
      platformByCategory,
      platformCategories,
      erpByModule,
      erpModules,
      allPlatformKeys,
      allErpModuleKeys,
      allErpCategoryKeys,
    };
  }, [data?.stepTypes, searchQuery, showDeprecated, gently]);

  const isSearching = searchQuery.trim().length > 0;

  const effectivePlatformKeys = isSearching
    ? allPlatformKeys
    : openPlatformCategories;

  const effectiveErpModules = isSearching ? allErpModuleKeys : openErpModules;
  const effectiveErpCategoryKeys = isSearching
    ? allErpCategoryKeys
    : openErpCategoryKeys;

  const hasContent = platformCategories.length > 0 || erpModules.length > 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6">
        <CircleNotchIcon className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-sm text-destructive">
        <T context="error message when workflow node library step types fail to load">
          Failed to load step types
        </T>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b space-y-2">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={gt('Search nodes...', {
              context: 'search input placeholder in workflow node library',
            })}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-8 h-8"
          />
        </div>
        {gently && (
          <button
            type="button"
            onClick={() => setShowDeprecated((v) => !v)}
            className={cn(
              'flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border transition-colors w-full',
              showDeprecated
                ? 'bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-900/30 dark:border-amber-600 dark:text-amber-300'
                : 'bg-muted/50 border-transparent text-muted-foreground hover:bg-muted',
            )}
          >
            <EyeSlashIcon className="w-3.5 h-3.5 flex-shrink-0" />
            <T context="toggle button to show deprecated workflow nodes in node library">
              Show deprecated nodes
            </T>
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {!hasContent ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            {searchQuery
              ? gt('No nodes match your search', {
                  context:
                    'empty state when workflow node library search returns no results',
                })
              : gt('No step types available', {
                  context:
                    'empty state when workflow node library has no step types',
                })}
          </div>
        ) : (
          <div className="flex flex-col gap-4 py-2 px-2">
            {platformCategories.length > 0 && (
              <section>
                <PlatformCategoryAccordion
                  categories={platformCategories}
                  stepsByCategory={platformByCategory}
                  openKeys={effectivePlatformKeys}
                  onOpenChange={setOpenPlatformCategories}
                />
              </section>
            )}

            {erpModules.length > 0 && (
              <section className="space-y-2">
                {platformCategories.length > 0 && (
                  <div className="flex items-center gap-2 px-1 pt-1">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                      <T context="section label for ERP-specific workflow nodes in node library">
                        ERP modules
                      </T>
                    </span>
                    <span className="flex-1 h-px bg-border" />
                  </div>
                )}
                <div className="space-y-2">
                  {erpModules.map((module) => {
                    const byCategory =
                      erpByModule.get(module) ?? new Map<string, StepType[]>();
                    const moduleCategoryKeys = sortCategoriesAlphabetical([
                      ...byCategory.keys(),
                    ]).map((cat) => erpCategoryAccordionKey(module, cat));
                    const moduleOpenCategoryKeys =
                      effectiveErpCategoryKeys.filter((k) =>
                        moduleCategoryKeys.includes(k),
                      );

                    return (
                      <ErpModuleSection
                        key={module}
                        module={module}
                        stepsByCategory={byCategory}
                        isOpen={effectiveErpModules.includes(module)}
                        openCategoryKeys={moduleOpenCategoryKeys}
                        onModuleOpenChange={(open) => {
                          setOpenErpModules((prev) =>
                            open
                              ? [...prev, module]
                              : prev.filter((m) => m !== module),
                          );
                          if (open) {
                            setOpenErpCategoryKeys((prev) => [
                              ...new Set([...prev, ...moduleCategoryKeys]),
                            ]);
                          }
                        }}
                        onCategoryOpenChange={(keys) => {
                          setOpenErpCategoryKeys((prev) => {
                            const withoutModule = prev.filter(
                              (k) => !k.startsWith(`erp::${module}::`),
                            );
                            return [...withoutModule, ...keys];
                          });
                        }}
                      />
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      <div className="px-3 py-2 border-t flex-shrink-0">
        <p className="text-[10px] text-muted-foreground text-center">
          <T context="hint at bottom of workflow node library panel">
            Drag and drop nodes onto the canvas
          </T>
        </p>
      </div>
    </div>
  );
}
